type SentryPluginError = Error & { status?: number; code?: string };

interface BuildErrorHandlerOptions {
  /**
   * HTTP timeout (seconds) for sentry-cli. Without this, the client-bundle
   * sourcemap upload can hang indefinitely on upstream issues and kill the
   * build mid-prerender (missing prerender-manifest.json). Defaults to 60.
   * Set to `0` to disable.
   */
  httpTimeoutSeconds?: number;
}

/**
 * errorHandler for @sentry/nextjs `withSentryConfig`.
 *
 * Policy: Sentry must never break a deploy. Transient network errors
 * (504, gateway timeout, ETIMEDOUT, ECONNRESET — including timeouts from
 * SENTRY_HTTP_TIMEOUT) are warned and swallowed across all environments.
 * Other errors still hard-fail so auth/config issues stay visible.
 *
 * Also sets `SENTRY_HTTP_TIMEOUT` if not already set, so sentry-cli hangs
 * surface as a normal error (which this handler then swallows) instead of
 * killing the build silently.
 */
export function createBuildErrorHandler(
  options: BuildErrorHandlerOptions = {},
) {
  const timeout = options.httpTimeoutSeconds ?? 60;
  if (timeout > 0 && !process.env.SENTRY_HTTP_TIMEOUT) {
    process.env.SENTRY_HTTP_TIMEOUT = String(timeout);
  }

  return function sentryBuildErrorHandler(
    err: SentryPluginError,
    _invokeErr: unknown,
    compilation?: { errors: Error[] },
  ): void {
    const message = err?.message || String(err);
    const isTransientNetwork =
      [408, 502, 503, 504, 524].includes(err?.status ?? 0) ||
      ['ETIMEDOUT', 'ECONNRESET', 'EAI_AGAIN', 'ECONNREFUSED'].includes(
        err?.code ?? '',
      ) ||
      /\b(408|502|503|504|524)\b|gateway timeout|bad gateway|service unavailable|ETIMEDOUT|ECONNRESET|EAI_AGAIN|ECONNREFUSED/i.test(
        message,
      );

    if (isTransientNetwork) {
      console.warn('⚠️ Sentry transient error (build continues):', message);
      return;
    }

    console.error('🚨 Sentry plugin error:', err);

    if (compilation) {
      compilation.errors.push(new Error(`Sentry upload failed: ${message}`));
    }

    console.error('🚨 Exiting build process with code 1');
    process.exit(1);
  };
}
